import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { apiGetHfBasicMedDataByPatientIdOptions } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { MedicalPlotModel } from "../../../models/plotting";
import { MedData, MedDataItem } from "../common/med-data";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { AddVitalsDialog } from "./add-vitals-dialog";
import { BasicMedicalTable } from "./basic-medical-table";

export const BasicMedicalRoute = observer(function BasicMedicalRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new MedData<MedDataItem>([], api.permissions, api.requireUserUUID()),
  );

  const [showAddDialog, setShowAddDialog] = useState(false);

  const { data: response, refetch } = useQuery(
    apiGetHfBasicMedDataByPatientIdOptions({
      path: { patient_id: patient.patientId },
    }),
  );
  const data = response?.medical_data ?? noMedicalData;

  useEffect(() => {
    if (response) model.updateData(response.medical_data);
  }, [response, model]);

  const plot = usePlot(
    () =>
      new MedicalPlotModel(
        model,
        ["weight", "dry weight", "temperature", "respiratory rate"],
        "plot-canvas",
      ),
  );

  return (
    <PatientSubpage title={t`Basic medical data`} loading={!model.initialized}>
      <PlotCanvas model={plot} />

      <BasicMedicalTable
        filterActions={
          <>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowAddDialog(true)}
            >
              <Trans>Add basic medical data</Trans>
            </Button>
            <PlotControls model={plot} />
          </>
        }
        api={api}
        data={data}
        onInvalidated={refetch}
        patient={patient}
        persistState
      />

      {showAddDialog && (
        <AddVitalsDialog
          app={props.app}
          variant="basics"
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});

const noMedicalData: MedDataItem[] = [];
